import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, openModal } from "risa-oasis-ui_v2";
import BadgeDropdown from "../../../../components/badgeDropdown/badgeDropdown";
import ImageViewerWithBadgesModal from "../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import { createDataForWriteBackTrail } from "../../../../components/modals/imageViewerWithBadgesModal/utils/createDataForWriteBackTrail";
import RaiseQueryModal from "../../../../components/modals/raiseQueryModal/raiseQuery";
import { BusniessOfficeNote } from "../../../../data-model/busniessOfficeNote";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { EVWriteBackStatus } from "../../../../enums/evBvWriteBackStatus";
import { ModalId } from "../../../../enums/modalId";
import { useAuthStatusDropdown } from "../../../../hooks/useAuthStatusDropdown";
import {
  setMetaData,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import InactiveWarningIcon from "../../../../svg/inactiveWarningIcon";
import { InfoIcon } from "../../../../svg/info-icon";
import RpaStatusCard from "../rpaStatusCard/rpaStatusCard";

export interface InactiveInsuranceProps {
  onCommentsChange: (comment: string) => void;
  queryType?: string;
  status?: string;
  id?: string;
  reports?: BusniessOfficeNote[];
  variant?: "inactive" | "unableToRetrieve" | "notSupported" | "fetching";
  /**
   * When set with `variant="unableToRetrieve"` or `variant="notSupported"`,
   * renders an "Update Manually" control so the parent can switch to local plan/financial editing.
   */
  onUpdateManually?: () => void;
  generalOrderDetails?: GeneralOrderDetails;
}

const InactiveInsurance = (props: InactiveInsuranceProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [writtenText, setWrittenText] = useState<string>("");
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modal,
  );
  useEffect(() => {
    if (openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL) {
      openModal(ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    setWrittenText(props?.queryType ?? "");
  }, [props?.queryType]);

  const { selectedAuthStatusBadge, handleAuthStatusChange, authStatusOptions } =
    useAuthStatusDropdown();
  const shouldShowRpaStatusCard = () => {
    if (props.status === EVWriteBackStatus.NOT_INITIATED) {
      return false;
    } else {
      return true;
    }
  };
  const handleRpaStatusCardIconClick = () => {
    props.status === EVWriteBackStatus.ERROR &&
      dispatch(setOpenedModalId({ id: ModalId.INSURANCE_ERROR_DIALOG }));
    props.status === EVWriteBackStatus.SUCCESS &&
      handleDisplayWriteBackTrailScreenshot(props?.id ?? "");
  };

  const handleDisplayWriteBackTrailScreenshot = async (id: string) => {
    try {
      dispatch(
        setOpenedModalId({
          id: ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL,
          metaData: {
            showLoading: true,
          },
        }),
      );
      const dataForWriteBackTrail = await createDataForWriteBackTrail(
        id,
        authStatusOptions,
      );
      dispatch(
        setMetaData({
          tabs: dataForWriteBackTrail.tabs,
          showLoading: false,
        }),
      );
    } catch (error) {
      dispatch(
        setMetaData({
          tabs: [],
          showLoading: false,
        }),
      );
    }
  };
  const isUnableToRetrieve = props.variant === "unableToRetrieve";
  const isNotSupported = props.variant === "notSupported";
  const isFetching = props.variant === "fetching";
  const isMessageOnlyVariant =
    isUnableToRetrieve || isNotSupported || isFetching;

  return (
    <div className="coverage-inactive-container h-full w-full overflow-hidden rounded-lg border border-primaryGray-15 bg-white">
      {isFetching ? (
        <div className="flex h-full flex-1 flex-col items-center justify-center overflow-auto p-4">
          <div className="flex w-full max-w-4xl flex-col items-center justify-center gap-3 px-4 py-6">
            <InfoIcon
              height="28"
              width="28"
              stroke="#0056D6"
              strokeWidth="1.5"
            />
            <p className="text-center text-small font-normal text-tertiaryBlue-2">
              Coverage Information is being fetched. Please come back later.
            </p>
          </div>
        </div>
      ) : isUnableToRetrieve ? (
        <div className="flex h-full flex-1 flex-col items-center justify-center gap-4 overflow-auto px-4 py-6 sm:px-6">
          <div className="icon--container relative mr-12 flex items-center justify-center">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.35]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgb(214 214 214 / 0.45) 1px, transparent 1px), linear-gradient(to bottom, rgb(214 214 214 / 0.45) 1px, transparent 1px)",
                backgroundSize: "10px 10px",
              }}
              aria-hidden
            />
            <InactiveWarningIcon width="18rem" height="18rem" />
          </div>
          <div className="message--container mt-1 flex w-full min-w-0 flex-col items-center justify-center gap-4">
            <div className="message--title text-center text-small font-bold leading-5 text-primaryGray-3">
              Unable to Retrieve Coverage
            </div>
            <div className="w-full min-w-0 break-words text-center text-small font-normal leading-relaxed text-primaryGray-3">
              We couldn&apos;t automatically fetch coverage details. Please
              verify directly through the payer portal.
            </div>
            {props.onUpdateManually && (
              <div className="trigger-button--container mt-1">
                <Button
                  disabled={false}
                  children="Update Manually"
                  onClick={props.onUpdateManually}
                  buttonType="secondary"
                  size="medium"
                />
              </div>
            )}
          </div>
        </div>
      ) : isNotSupported ? (
        <div className="flex h-full flex-1 flex-col items-center gap-8 overflow-auto px-6 py-10">
          <div className="relative flex h-[17rem] w-full max-w-[20rem] items-center justify-center">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.35]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgb(214 214 214 / 0.45) 1px, transparent 1px), linear-gradient(to bottom, rgb(214 214 214 / 0.45) 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
              aria-hidden
            />
            <div
              className="relative flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center rounded-sm border border-[#2E2E2E] bg-white shadow-[5px_5px_0_0_#2E2E2E]"
              aria-hidden
            >
              <InfoIcon
                height="32"
                width="32"
                stroke="#E0CE00"
                strokeWidth="1.4"
              />
            </div>
          </div>
          <div className="message--container flex max-w-lg flex-col items-center justify-center gap-3 px-4">
            <div className="text-center text-h11 font-semibold text-primaryGray-1">
              Automated Verification Unavailable
            </div>
            <div className="text-center text-small font-normal leading-relaxed text-primaryGray-3">
              We don&apos;t currently support automated coverage retrieval for
              this payer. Please check the payer portal for details.
            </div>
            {props.onUpdateManually && (
              <div className="trigger-button--container mt-1">
                <Button
                  disabled={false}
                  children="Update Manually"
                  onClick={props.onUpdateManually}
                  buttonType="secondary"
                  size="medium"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-1 flex-col items-center gap-4 overflow-auto px-4 py-6 sm:px-6">
          <div className="icon--container mr-12">
            <InactiveWarningIcon width="18rem" height="18rem" />
          </div>
          <div className="message--container mt-1 flex w-full min-w-0 flex-col items-center justify-center gap-4">
            <div className="message--title text-center text-small text-primaryGray-3">
              The Insurance is inactive for the date <br /> of service
            </div>
            {shouldShowRpaStatusCard() ? (
              <RpaStatusCard
                status={props.status ?? ""}
                handleIconClick={handleRpaStatusCardIconClick}
              />
            ) : (
              <div className="flex items-center justify-center gap-4">
                <div className="trigger-button--container">
                  <Button
                    disabled={false}
                    children={"Trigger RPA"}
                    onClick={() => {
                      dispatch(
                        setOpenedModalId({
                          id: ModalId.INSURANCE_ERROR_DIALOG,
                        }),
                      );
                    }}
                    buttonType={"secondary"}
                    size={"medium"}
                  />
                </div>
              </div>
            )}
            <div className="mt-2 flex h-10 w-fit items-center gap-2 rounded border-[1px] border-solid border-primaryGray-14 bg-white px-3.5 py-2.5 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]">
              <div className="text-sm font-normal text-primaryGray-1">
                Select Auth Status
              </div>
              <BadgeDropdown
                selectedBadge={selectedAuthStatusBadge}
                badgeList={authStatusOptions}
                onClick={handleAuthStatusChange}
              />
            </div>
          </div>
        </div>
      )}

      {!isMessageOnlyVariant &&
        openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
          <ImageViewerWithBadgesModal
            tabs={metaData?.tabs ?? []}
            showLoading={metaData?.showLoading ?? false}
            title="Write Back Trail"
          />
        )}
      {!isMessageOnlyVariant &&
        openedModalId === ModalId.INSURANCE_ERROR_DIALOG && (
          <RaiseQueryModal
            onCommentsChange={(comment) => {
              setWrittenText(comment);
              props?.onCommentsChange(comment);
            }}
            queryType={
              writtenText ??
              "Authmate Case: Insurance is inactive, please check"
            }
            reports={props?.reports ?? []}
            generalOrderDetails={props?.generalOrderDetails}
          />
        )}
    </div>
  );
};

export default InactiveInsurance;
